import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Users2, Plus, Pencil, KeyRound, Trash2, ShieldCheck, Eye } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar, AVATAR_COLOR_KEYS, AVATAR_BG } from "@/components/UserAvatar";

const initials = (name = "") =>
  name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "U";

const emptyForm = { full_name: "", email: "", password: "", role: "", company_scope: ["VDV", "ET"], active: true, recibe_leads: true, readonly: false, avatar_color: "" };
const salesDefault = (role) => ["travel_specialist", "director_sales"].includes(role);

export default function UsersPage() {
  const qc = useQueryClient();
  const { user: me, startImpersonation } = useAuth();
  const isAdmin = me?.is_admin;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [resetUser, setResetUser] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [deleteUser, setDeleteUser] = useState(null);
  const [impersonating, setImpersonating] = useState(null); // user_id que se está lanzando

  const usersQ = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });
  const rolesQ = useQuery({ queryKey: ["roles"], queryFn: async () => (await api.get("/roles")).data });
  const roles = rolesQ.data || [];
  const roleName = (key) => roles.find((r) => r.key === key)?.name || key;

  const invalidate = () => qc.invalidateQueries({ queryKey: ["users"] });

  const createMut = useMutation({
    mutationFn: (body) => api.post("/users", body),
    onSuccess: () => { toast.success("Usuario creado"); invalidate(); setDialogOpen(false); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, body }) => api.put(`/users/${id}`, body),
    onSuccess: () => { toast.success("Usuario actualizado"); invalidate(); setDialogOpen(false); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const resetMut = useMutation({
    mutationFn: ({ id, new_password }) => api.post(`/users/${id}/reset-password`, { new_password }),
    onSuccess: () => { toast.success("Contraseña reseteada"); setResetUser(null); setResetPw(""); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/users/${id}`),
    onSuccess: () => { toast.success("Usuario eliminado"); invalidate(); setDeleteUser(null); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const openCreate = () => {
    setEditing(null);
    const r = roles[0]?.key || "";
    setForm({ ...emptyForm, role: r, recibe_leads: salesDefault(r) });
    setDialogOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ full_name: u.full_name, email: u.email, password: "", role: u.role, company_scope: u.company_scope || [], active: u.active, recibe_leads: !!u.recibe_leads, readonly: !!u.readonly, avatar_color: u.avatar_color || "" });
    setDialogOpen(true);
  };

  const toggleCompany = (co) => {
    setForm((f) => ({
      ...f,
      company_scope: f.company_scope.includes(co) ? f.company_scope.filter((c) => c !== co) : [...f.company_scope, co],
    }));
  };

  const submitForm = (e) => {
    e.preventDefault();
    if (!form.full_name || form.full_name.length < 2) { toast.error("Nombre obligatorio"); return; }
    if (editing) {
      updateMut.mutate({ id: editing.id, body: { full_name: form.full_name, role: form.role, company_scope: form.company_scope, active: form.active, recibe_leads: form.recibe_leads, readonly: form.readonly, avatar_color: form.avatar_color || null } });
    } else {
      if (!form.email || !form.password || form.password.length < 6) { toast.error("Email y contraseña (mín. 6) obligatorios"); return; }
      createMut.mutate(form);
    }
  };

  const users = usersQ.data || [];

  return (
    <div className="space-y-5" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
            <Users2 className="h-6 w-6 text-primary" /> Usuarios
          </h1>
          <p className="text-sm text-muted-foreground">Alta, edición, roles y reseteo de contraseñas.</p>
        </div>
        <Button onClick={openCreate} data-testid="add-user-button"><Plus className="mr-2 h-4 w-4" /> Nuevo usuario</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Usuario</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead className="w-[110px]">Empresas</TableHead>
                <TableHead className="w-[100px]">Estado</TableHead>
                <TableHead className="w-[160px] text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {usersQ.isLoading && Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}><TableCell colSpan={5}><Skeleton className="h-9 w-full" /></TableCell></TableRow>
              ))}
              {users.map((u) => (
                <TableRow key={u.id} data-testid="user-row">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <UserAvatar name={u.full_name} color={u.avatar_color || "slate"} size="md" />
                      <div className="min-w-0 leading-tight">
                        <p className="truncate text-sm font-medium">{u.full_name}{u.id === me?.id && <span className="ml-1 text-xs text-muted-foreground">(tú)</span>}</p>
                        <p className="truncate text-xs text-muted-foreground">{u.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="flex items-center gap-1.5 text-sm">
                      {(u.is_admin || u.is_leadership) && <ShieldCheck className="h-3.5 w-3.5 text-primary" />}
                      {roleName(u.role)}
                    </span>
                  </TableCell>
                  <TableCell><span className="text-xs text-muted-foreground">{(u.company_scope || []).join(" · ") || "—"}</span></TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1">
                      {u.active
                        ? <Badge variant="secondary" className="bg-success/15 text-success">Activo</Badge>
                        : <Badge variant="secondary" className="bg-muted text-muted-foreground">Inactivo</Badge>}
                      {u.readonly && (
                        <Badge variant="secondary" className="border border-amber-300 bg-amber-100 text-amber-900" title="Cuenta de solo lectura">
                          Solo lectura
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {isAdmin && u.id !== me?.id && u.active && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-amber-600 hover:bg-amber-100 hover:text-amber-700 dark:hover:bg-amber-950/50"
                          disabled={impersonating === u.id}
                          onClick={async () => {
                            setImpersonating(u.id);
                            try { await startImpersonation(u.id); }
                            catch (e) { toast.error(apiErrorMessage(e)); setImpersonating(null); }
                          }}
                          data-testid={`impersonate-user-${u.email}`}
                          aria-label={`Ver la app como ${u.full_name}`}
                          title={`Ver la app como ${u.full_name} (solo lectura, 30 min)`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`} aria-label="Editar"><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setResetUser(u); setResetPw(""); }} data-testid={`reset-user-${u.email}`} aria-label="Resetear contraseña"><KeyRound className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" disabled={u.id === me?.id} onClick={() => setDeleteUser(u)} data-testid={`delete-user-${u.email}`} aria-label="Eliminar"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar usuario" : "Nuevo usuario"}</DialogTitle>
            <DialogDescription>{editing ? "Actualiza los datos del usuario." : "El usuario deberá cambiar la contraseña al primer acceso."}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitForm} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="u-name">Nombre completo</Label>
              <Input id="u-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="user-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="u-email">Email</Label>
              <Input id="u-email" type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" />
            </div>
            {!editing && (
              <div className="space-y-1.5">
                <Label htmlFor="u-pw">Contraseña inicial</Label>
                <Input id="u-pw" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, recibe_leads: editing ? form.recibe_leads : salesDefault(v) })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue placeholder="Selecciona un rol" /></SelectTrigger>
                <SelectContent>
                  {roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Color de avatar</Label>
              <div className="flex flex-wrap items-center gap-2" data-testid="user-color-picker">
                <UserAvatar name={form.full_name || "?"} color={form.avatar_color || "slate"} size="md" />
                {AVATAR_COLOR_KEYS.map((c) => (
                  <button key={c} type="button" onClick={() => setForm({ ...form, avatar_color: c })}
                    aria-label={c} data-testid={`user-color-${c}`}
                    className={`h-6 w-6 rounded-full ring-offset-2 transition ${AVATAR_BG[c]} ${form.avatar_color === c ? "ring-2 ring-foreground" : "hover:scale-110"}`} />
                ))}
              </div>
            </div>
            <div className="flex gap-4">
              <label className="flex flex-1 items-center justify-between rounded-md bg-muted/50 px-3 py-2">
                <span className="text-sm font-medium">Cuenta activa</span>
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="user-active-switch" />
              </label>
              <label className="flex flex-1 items-center justify-between rounded-md bg-muted/50 px-3 py-2" title="Aparecerá como fila en el panel de Distribución de Leads">
                <span className="text-sm font-medium">Recibe leads</span>
                <Switch checked={form.recibe_leads} onCheckedChange={(v) => setForm({ ...form, recibe_leads: v })} data-testid="user-leads-switch" />
              </label>
            </div>
            <label className="flex items-start justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5"
                   title="Cuenta de solo lectura: puede ver todo pero no crear/editar/borrar. Ideal para auditores externos o cuentas de IA (ChatGPT).">
              <div className="flex-1">
                <div className="text-sm font-medium text-amber-900">Solo lectura</div>
                <div className="text-[11px] text-amber-800">
                  Bloquea toda escritura (crear, editar, borrar) y oculta módulos sensibles: RRHH, nóminas, API keys, impersonación, auditoría.
                </div>
              </div>
              <Switch checked={form.readonly} onCheckedChange={(v) => setForm({ ...form, readonly: v })} data-testid="user-readonly-switch" />
            </label>
            <div className="space-y-1.5">
              <Label>Empresas</Label>
              <div className="flex gap-4">
                {["VDV", "ET"].map((co) => (
                  <label key={co} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.company_scope.includes(co)} onCheckedChange={() => toggleCompany(co)} data-testid={`company-${co}`} />
                    {co}
                  </label>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={createMut.isPending || updateMut.isPending} data-testid="user-save-button">
                {editing ? "Guardar cambios" : "Crear usuario"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetUser} onOpenChange={(v) => !v && setResetUser(null)}>
        <DialogContent className="sm:max-w-md" data-testid="reset-dialog">
          <DialogHeader>
            <DialogTitle>Resetear contraseña</DialogTitle>
            <DialogDescription>Nueva contraseña para <span className="font-medium text-foreground">{resetUser?.email}</span>. Se le pedirá cambiarla al entrar.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="reset-pw">Nueva contraseña</Label>
            <Input id="reset-pw" type="text" value={resetPw} onChange={(e) => setResetPw(e.target.value)} data-testid="reset-password-input" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResetUser(null)}>Cancelar</Button>
            <Button onClick={() => { if (resetPw.length < 6) { toast.error("Mínimo 6 caracteres"); return; } resetMut.mutate({ id: resetUser.id, new_password: resetPw }); }} disabled={resetMut.isPending} data-testid="reset-confirm-button">Resetear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteUser} onOpenChange={(v) => !v && setDeleteUser(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar usuario?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará <span className="font-medium text-foreground">{deleteUser?.email}</span>. Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => deleteMut.mutate(deleteUser.id)} data-testid="delete-confirm-button">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
